import {
  Button,
  DatePickerField,
  Divider,
  Flex,
  FormProvider,
  Grid,
  SelectField,
  Stack,
  Text,
  TextField,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { YES_NO_OPTIONS, YesNoOption, exploitationTypes } from '@bcba/types';
import { useUpsertProductiveAffidavit } from './useUpsertProductiveAffidavit';
import { CommonProductiveAffidavitSerializer } from '@bcba/serializers';
import { usePendingChangesAlert } from '../../../../../contexts/hooks';

const formId = 'upsert-productive-affidavit';

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  facilityUuid: string;
  selectedAffidavit?: CommonProductiveAffidavitSerializer;
}

export const UpsertProductiveAffidavit = ({
  onSuccess,
  onCancel,
  facilityUuid,
  selectedAffidavit,
}: Props) => {
  const { t } = useTranslation();

  const { formMethods, onSubmit, isPendingUpsertAffidavit } =
    useUpsertProductiveAffidavit({
      onSuccess,
      facilityUuid,
      selectedAffidavit,
    });

  const {
    handleSubmit,
    formState: { isDirty },
  } = formMethods;
  usePendingChangesAlert(isDirty);
  const exploitationTypeOptions = exploitationTypes.map((et) => ({
    label: t(`exploitationType.${et}`),
    value: et,
  }));
  const renspaCurrentOptions = [
    { label: t('common.yes'), value: YES_NO_OPTIONS.YES },
    { label: t('common.no'), value: YES_NO_OPTIONS.NO },
  ];

  const isUpdating = Boolean(selectedAffidavit);

  const sectionTitle = isUpdating
    ? t('explorer.productiveFacilities.editAffidavit')
    : t('explorer.productiveFacilities.addAffidavit');
  const confirmLabel = isUpdating ? t('common.save') : t('common.add');

  return (
    <Stack divider={<Divider />} gap={4}>
      <Text size={'xl'}>{sectionTitle}</Text>
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider {...formMethods}>
          <Stack gap={8}>
            <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
              <TextField
                name={'hectares'}
                label={t(
                  'productiveFacilities.upsert.productiveHectares.label',
                )}
                placeholder={t(
                  'productiveFacilities.upsert.productiveHectares.placeholder',
                )}
                errorMessageFormatter={t}
                rhfOptions={{
                  required: true,
                  setValueAs: (value) => (value ? Number(value) : undefined),
                  min: 0,
                }}
                type="number"
              />
              <SelectField
                name={'exploitationType'}
                label={t('productiveFacilities.upsert.exploitationType.label')}
                placeholder={t(
                  'productiveFacilities.upsert.exploitationType.placeholder',
                )}
                options={exploitationTypeOptions}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
              <DatePickerField
                name={'startDate'}
                label={t('productiveFacilities.upsert.startDate.label')}
                placeholder={t(
                  'productiveFacilities.upsert.startDate.placeholder',
                )}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
              <DatePickerField
                name={'endDate'}
                label={t('productiveFacilities.upsert.endDate.label')}
                placeholder={t(
                  'productiveFacilities.upsert.endDate.placeholder',
                )}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
              <TextField
                name={'renspa'}
                label={t('productiveFacilities.upsert.renspa.label')}
                placeholder={t(
                  'productiveFacilities.upsert.renspa.placeholder',
                )}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
              <SelectField
                name={'renspaIsCurrent'}
                label={t('productiveFacilities.upsert.currentRenspa.label')}
                placeholder={t(
                  'productiveFacilities.upsert.currentRenspa.placeholder',
                )}
                errorMessageFormatter={t}
                options={renspaCurrentOptions}
                rhfOptions={{
                  setValueAs: (value: YesNoOption) =>
                    value === YES_NO_OPTIONS.YES,
                }}
              />
            </Grid>
            <Flex justifyContent={'end'} gap={4}>
              <Button variant={'secondary'} onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={'primary'}
                type={'submit'}
                isLoading={isPendingUpsertAffidavit}
                isDisabled={!isDirty}
              >
                {confirmLabel}
              </Button>
            </Flex>
          </Stack>
        </FormProvider>
      </form>
    </Stack>
  );
};
